/**
 * DocumentEditor — /documents/$id/edit
 *
 * A reliable, always-available in-app rich-text editor for the document body
 * (loads + saves via /documents/{id}/content). OnlyOffice is offered as an
 * optional enhancement ("Open in OnlyOffice") with its api.js resolved against
 * the API base — previously it loaded from the relative /onlyoffice on the
 * static SPA host (which has no proxy), so it always failed. The in-app editor
 * means editing works regardless of the Document Server's server-side wiring.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@edms/design-system';
import { useDocumentDetailQuery } from '../DocumentDetail/useDocumentDetailQuery';
import { useEditSession } from './useEditSession';
import { useDocumentContentQuery, useSaveContentMutation } from './useDocumentContent';
import styles from './DocumentEditor.module.css';

export interface DocumentEditorProps {
  documentId: string;
  onClose?: () => void;
}

type ToolCmd = { cmd: string; value?: string; label: string; icon: string };
const TOOLS: ToolCmd[][] = [
  [
    { cmd: 'bold', label: 'Bold', icon: 'B' },
    { cmd: 'italic', label: 'Italic', icon: 'I' },
    { cmd: 'underline', label: 'Underline', icon: 'U' },
  ],
  [
    { cmd: 'formatBlock', value: 'H2', label: 'Heading', icon: 'H2' },
    { cmd: 'formatBlock', value: 'H3', label: 'Subheading', icon: 'H3' },
    { cmd: 'formatBlock', value: 'P', label: 'Paragraph', icon: '¶' },
  ],
  [
    { cmd: 'insertUnorderedList', label: 'Bulleted list', icon: '•' },
    { cmd: 'insertOrderedList', label: 'Numbered list', icon: '1.' },
  ],
  [
    { cmd: 'undo', label: 'Undo', icon: '↶' },
    { cmd: 'redo', label: 'Redo', icon: '↷' },
  ],
];

export function DocumentEditor({ documentId, onClose }: DocumentEditorProps) {
  const { t } = useTranslation();
  const session = useEditSession(documentId);
  const detailQ = useDocumentDetailQuery(documentId);
  const contentQ = useDocumentContentQuery(documentId);
  const saveM = useSaveContentMutation(documentId);

  const editorRef = useRef<HTMLDivElement | null>(null);
  const seededRef = useRef(false);
  const [dirty, setDirty] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  const viewOnly = session.data?.editorMode === 'view';
  const docTitle = detailQ.data?.doc_number ?? documentId;
  const docSubtitle = detailQ.data?.title ?? '';

  // Seed the editable surface once content arrives (avoid clobbering edits).
  useEffect(() => {
    if (seededRef.current) return;
    if (contentQ.data && editorRef.current) {
      editorRef.current.innerHTML = contentQ.data.content_html || '';
      setSavedAt(contentQ.data.updated_at_utc);
      seededRef.current = true;
    }
  }, [contentQ.data]);

  const exec = useCallback((c: ToolCmd) => {
    if (viewOnly) return;
    editorRef.current?.focus();
    try { document.execCommand(c.cmd, false, c.value); } catch { /* ignore */ }
    setDirty(true);
  }, [viewOnly]);

  const handleSave = useCallback(async () => {
    if (!editorRef.current || viewOnly) return;
    try {
      const res = await saveM.mutateAsync(editorRef.current.innerHTML);
      setSavedAt(res.updated_at_utc);
      setDirty(false);
    } catch { /* surfaced via saveM.isError below */ }
  }, [saveM, viewOnly]);

  const handleClose = useCallback(() => {
    if (onClose) return onClose();
    if (window.history.length > 1) window.history.back();
    else window.location.assign('/');
  }, [onClose]);

  const savedLabel = saveM.isPending
    ? t('docEditor.saving', { defaultValue: 'Saving…' })
    : dirty
      ? t('docEditor.unsaved', { defaultValue: 'Unsaved changes' })
      : savedAt
        ? t('docEditor.savedAt', { defaultValue: 'Saved {{t}}', t: new Date(savedAt).toLocaleTimeString() })
        : t('docEditor.savedClean', { defaultValue: 'All changes saved' });

  return (
    <main className={styles.editorPage}>
      <header className={styles.toolbar}>
        <div className={styles.titleBlock}>
          <span className={styles.eyebrow}>{t('docEditor.docPrefix', { defaultValue: 'Editing' })}</span>
          <strong className={styles.title}>{docTitle}</strong>
          {docSubtitle && <span className={styles.subtitle}>{docSubtitle}</span>}
        </div>
        <div className={styles.headerActions}>
          <span className={`${styles.saveState} ${dirty ? styles.saveDirty : styles.saveClean}`}>{savedLabel}</span>
          {!viewOnly && (
            <Button variant="primary" onClick={() => void handleSave()} isLoading={saveM.isPending} disabled={!dirty}>
              {t('docEditor.save', { defaultValue: 'Save' })}
            </Button>
          )}
          <Button variant="secondary" onClick={handleClose}>{t('docEditor.done', { defaultValue: 'Done' })}</Button>
        </div>
      </header>

      {viewOnly && session.data?.viewOnlyReason && (
        <div role="status" className={styles.viewBanner}>
          <span aria-hidden>🔒</span>
          <strong>{t('docEditor.viewOnlyTitle', { defaultValue: 'View-only' })}</strong>
          <span className={styles.viewReason}>{session.data.viewOnlyReason}</span>
        </div>
      )}
      {saveM.isError && (
        <div role="alert" className={styles.errBanner}>{t('docEditor.saveFailed', { defaultValue: 'Could not save — retry in a moment.' })}</div>
      )}

      {(
        <div className={styles.editorWrap}>
          {!viewOnly && (
            <div className={styles.formatBar} role="toolbar" aria-label={t('docEditor.formatAria', { defaultValue: 'Formatting' })}>
              {TOOLS.map((group, gi) => (
                <div key={gi} className={styles.toolGroup}>
                  {group.map((c) => (
                    <button
                      key={c.label}
                      type="button"
                      className={styles.toolBtn}
                      title={c.label}
                      aria-label={c.label}
                      onMouseDown={(e) => { e.preventDefault(); exec(c); }}
                    >
                      {c.icon}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          )}
          {contentQ.isLoading ? (
            <div className={styles.paper}><p className={styles.muted}>{t('docEditor.loadingBody', { defaultValue: 'Loading document…' })}</p></div>
          ) : (
            <div
              ref={editorRef}
              className={`${styles.paper} ${viewOnly ? styles.paperReadonly : ''}`}
              contentEditable={!viewOnly}
              suppressContentEditableWarning
              spellCheck
              onInput={() => setDirty(true)}
              aria-label={t('docEditor.bodyAria', { defaultValue: 'Document body' })}
            />
          )}
        </div>
      )}
    </main>
  );
}
